import * as React from "react";
import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ClipLoader from "react-spinners/ClipLoader";
import { Conversation } from "../../api/models";

interface DeleteDialogProps {
    open: boolean;
    conversation: Conversation | null;
    onClose: () => void;
    onConfirm: () => void;
    isDeleting?: boolean;
    onAfterLeave?: () => void;
}

export function DeleteDialog({ open, conversation, onClose, onConfirm, isDeleting, onAfterLeave }: DeleteDialogProps) {
    const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

    if (!open && !conversation) return null;

    return (
        <Transition appear show={open} as={Fragment} afterLeave={onAfterLeave}>
            <Dialog className="relative z-[10000]" initialFocus={cancelButtonRef} onClose={onClose}>
                <Transition.Child
                    as="div" // render a real element so className can be applied
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as="div"
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden bg-eq-white text-left align-middle shadow-2xl transition-all">
                                <div className="p-4">
                                    <Dialog.Title className="text-base font-semibold text-eq-grey">
                                        Delete conversation?
                                    </Dialog.Title>

                                    <div className="mt-2">
                                        <p className="text-sm text-eq-grey">
                                            This will delete <span className="font-semibold">{conversation?.title}</span> and all of its messages. This action cannot be undone.
                                        </p>
                                    </div>

                                    <div className="mt-4 flex justify-end gap-2">
                                        <button
                                            ref={cancelButtonRef}
                                            type="button"
                                            className="eq-secondary-button transition hover:bg-eq-secondary-hover"
                                            onClick={onClose}
                                            disabled={!!isDeleting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="eq-primary-button transition hover:bg-eq-primary-hover flex items-center justify-center gap-2"
                                            onClick={onConfirm}
                                            disabled={!!isDeleting}
                                        >
                                            {isDeleting ? <ClipLoader size={16} color="white" /> : "Delete"}
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}